import React, { useState, useRef, useEffect } from 'react';
import { TimelineItemType } from 'types/TimelineTypes';
import { UserType } from '../../views/users/UserTypes';
import { Payment } from '../../views/invoices/InvoiceTypes';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import axios from 'axios';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import InvoicePaymentForm from '../../views/invoices/InvoicePaymentForm';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import PulldownMenu from 'components/layout/PulldownMenu';
import { saveData } from 'services/api/saveData';
import { convertDecimalToPrice } from 'services/utils/convertDecimalToPrice';
import { useSettings } from 'services/settings/SettingsContext';
import '../../style/scss/tooltip.scss';

interface TimelinePaymentProps {
    item: TimelineItemType;
    payment: Payment;
    timeline_page_id: number;
    registratedBy?: UserType | null;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const TimelinePayment: React.FC<TimelinePaymentProps> = ({ 
    item, payment, timeline_page_id, registratedBy, onSubmitSuccess
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { userHash } = useAuthContext();
    const { userLocale } = useSettings();
    const { initializeModal } = useModal();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isExpandable, setIsExpandable] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const helperTextRef = useRef<HTMLDivElement>(null);
    
    // Format the date and amount
    const paymentDate = formatTimelineDate(item.ordering_date, true);
    const paymentAmount = convertDecimalToPrice(payment.amount, payment.currency, userLocale);

    // Expand the note
    const toggleIsExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Shows the text expand icon if the helpertext element is higher than the visible text element
    useEffect(() => {
        const timer = setTimeout(() => {
            if (textRef.current && helperTextRef.current) {
                const visibleTextHeight = textRef.current.offsetHeight;
                const helperTextHeight = helperTextRef.current.offsetHeight;
                setIsExpandable(helperTextHeight > visibleTextHeight);
            }
        }, 100);
    
        return () => clearTimeout(timer);
    }, [payment.note]);

    // Delete the payment in the database
    const deletePayment = async () => {
        try {
            // Call the delete task function on the backend
            const response = await saveData({ 
                apiUrl: `delete_payment/${payment.id}`,
                method: 'patch',
                data: {},
                source: axios.CancelToken.source()
            })

            // Handle successful response
            if (response && response.status === 200) {
                // Show success alert
                setFloatingAlert({ 'type': 'success' });

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'payment'
                });
            }
        } catch (error) {
            console.error('Deletion failed');
        }
    };

    // Handle editing of a task
    const handleEditing = () => {
        setIsEditing(true);
  
        initializeModal(
            <InvoicePaymentForm
                linkedItem={{
                    invoice: timeline_page_id,
                    ...(userHash ? { registrated_by_user_hash: userHash } : {})
                }}
            />, 
            { 
                itemId: payment.id,
                modalSize: 'small',
                title: t('button.edit_object_label', { object_name: t('invoice.payment.object_name.singular') }),
                onClose: () => setIsEditing(false),
                onSuccess: (response?: Record<string, any>) => {
                    if (response && response.payment) {
                        const newItem = {
                            ...(response as Partial<TimelineItemType>),
                            timeline_object_type: 'payment',
                        } as TimelineItemType;
                
                        onSubmitSuccess?.(newItem);
                    }
                }
            }
        );
    };

    return (
        <div className='timeline-item payment'>
            <div className='payment-content'>
                <div>
                    <div className='payment-text'>
                        {t('invoice.general.payment_timeline_text', { 
                            amount: paymentAmount, 
                            payment_method: t(`invoice.payment_method.${payment.payment_method}`)  
                        })}
                    </div>
                    <div className='payment-info'>
                        <div className='left-items'>
                            {paymentDate}
                            {registratedBy && registratedBy.full_name && (
                                <>
                                    <span style={{ margin: '0 5px' }}>•</span>
                                    <span>{registratedBy.full_name}</span>
                                </>
                            )}
                            <PulldownMenu
                                tooltipText='general.modify'
                                menuItems={[
                                    { label: 'general.edit_label', onClick: handleEditing },
                                    { label: 'general.delete', onClick: deletePayment }
                                ]}
                            />
                        </div>
                    </div>
                </div>
                {payment.note && (
                    <div className='payment-note'>
                        <div className='left-items' style={{ position: 'relative' }}>
                            <p className='note-text'
                                ref={textRef} 
                                dangerouslySetInnerHTML={{ __html: payment.note }}
                                style={!isExpanded ? { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: '1', WebkitBoxOrient: 'vertical' } : {}}>
                            </p>
                            {/* Help element to determine if the text expand icon needs to show */}
                            <p className='note-text' 
                                ref={helperTextRef} 
                                style={{ position: 'absolute', visibility: 'hidden', height: 'auto', width: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: payment.note }}>
                            </p>
                        </div>
                        <div className='right-items'>
                            {isExpandable && !isEditing &&
                                <div className='tooltip-icon expand-icon' onClick={toggleIsExpanded}>
                                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                    <span className="tooltip">{isExpanded ? t('general.shrink_label') : t('general.expand_label')}</span>
                                </div>
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TimelinePayment;